import { type FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import Tooltip from 'app/components/Tooltip';
import StatValue from 'app/components/StatValue';
import { round } from 'app/utils/number';
import { isNumber } from 'app/utils/types';

import style from './style.module.scss';

interface Props {
  variation?: GaugeVariation;
  weak?: boolean;
  value: number | undefined;
  orientation?: Orientation;
  hideValue?: boolean;
  absoluteValue?: boolean;
  displayFunction?: (val: number) => string;
  forceGaugeDisplay?: boolean;
  valuePosition?: 'start' | 'end';
  joined?: boolean;
}

const LineGauge: FC<Props> = ({
  variation = 'default',
  weak = false,
  value,
  orientation = 'normal',
  hideValue = false,
  absoluteValue = false,
  displayFunction,
  forceGaugeDisplay = false,
  valuePosition = 'end',
  joined = false
}) => {
  const { t } = useTranslation();

  const percentValue = isNumber(value) && value <= 1 ? `${round(100 * value, 0)}%` : `${value}%`;

  const lineGaugeContainer = classNames(style.lineGaugeContainer, {
    [style.reversed]:
      orientation === 'reversed' ? valuePosition === 'end' : valuePosition === 'start'
  });

  const lineValue = classNames(style.value, style[variation], {
    [style.weakValue]: weak,
    [style.reversed]:
      orientation === 'reversed' ? valuePosition === 'end' : valuePosition === 'start'
  });

  const lineGauge = classNames(style.lineGauge, style[variation], {
    [style.reversed]: orientation === 'reversed',
    [style.joined]: joined
  });

  const lineGaugeValue = classNames(style.lineGaugeValue, style[variation], {
    [style.weak]: weak,
    [style.reversed]: orientation === 'reversed',
    [style.joined]: joined
  });

  const noValue = classNames(style.noValue, style[variation], {
    [style.reversed]: orientation === 'reversed' && valuePosition === 'end'
  });

  return (
    <div className={lineGaugeContainer}>
      {isNumber(value) ? (
        <>
          {(!hideValue || absoluteValue) && (
            <div className={lineValue}>
              {absoluteValue ? (
                <StatValue value={value} displayFunction={displayFunction} />
              ) : (
                percentValue
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {forceGaugeDisplay ? (
            <div className={lineValue}>-</div>
          ) : (
            <Tooltip
              content={t('component_line-gauge_no-data_tooltip')}
              alignment={orientation === 'reversed' ? 'start' : 'end'}
              className={noValue}
            >
              {t('component_line-gauge_no-data')}
            </Tooltip>
          )}
        </>
      )}
      {!absoluteValue && (isNumber(value) || forceGaugeDisplay) && (
        <div className={lineGauge}>
          <div className={lineGaugeValue} style={{ width: percentValue }} />
        </div>
      )}
    </div>
  );
};

LineGauge.displayName = 'LineGauge';

export default LineGauge;
